// HomePage — Discover / Home. Wired to /airports/search via SearchWidget.
// Elevated: added StoriesStrip (live dispatches), DealBanner (deal-of-the-day),
// TrendingGrid (4-col hover-lift cards). Recent searches strip shown if authed.
// Design: WebDiscover from web-screens.jsx — full-bleed hero, search pill.

import { useState } from 'react';
import { SearchWidget } from '@/components/SearchWidget';
import { useIsMobile } from '@/hooks/useIsMobile';
import { SiteFooter } from '@/components/SiteFooter';
import { StoriesStrip } from '@/components/StoriesStrip';
import { DealBanner } from '@/components/DealBanner';
import { TrendingGrid } from '@/components/TrendingGrid';
import { notYetWiredToast } from '@/components/primitives';
import { useAuth } from '@/lib/auth';

const IMG = (id: string, w = 1200) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

const HERO_DESTINATIONS = [
  { id: 'bali',      city: 'Bali',      country: 'Indonesia', code: 'DPS', price: 34800, tag: 'TRENDING',   img: IMG('1537996194471-e657df975ab4') },
  { id: 'tokyo',     city: 'Tokyo',     country: 'Japan',     code: 'HND', price: 57100, tag: 'HOT',        img: IMG('1540959733332-eab4deabeeaf') },
  { id: 'lisbon',    city: 'Lisbon',    country: 'Portugal',  code: 'LIS', price: 25900, tag: 'WEEKEND',    img: IMG('1555881400-74d7acaacd8b') },
  { id: 'reykjavik', city: 'Reykjavík', country: 'Iceland',   code: 'KEF', price: 44800, tag: 'BUCKETLIST', img: IMG('1504829857797-ddff29c27927') },
];

// Recent searches shown for authed users (mock — TODO(search-history-PR))
const RECENT_SEARCHES = [
  { from: 'BLR', to: 'BKK', label: 'Bangkok', date: '12 Jul' },
  { from: 'BLR', to: 'DXB', label: 'Dubai',   date: '3 Aug' },
  { from: 'DEL', to: 'LHR', label: 'London',  date: '15 Aug' },
];

const T = {
  bg: '#0A0F1C', surface: '#131C30', ink: '#F4F3F0', inkDim: '#8C98B0',
  primary: '#E8441A', accent: '#4B8EDB', line: 'rgba(255,255,255,0.07)', lineHi: 'rgba(255,255,255,0.14)',
};

const F = '"Inter Variable","Inter",system-ui,sans-serif';
const DISPLAY = '"Fraunces Variable","Fraunces",Georgia,serif';

export function HomePage() {
  const [destIdx, setDestIdx] = useState(0);
  const { status } = useAuth();
  const isMobile = useIsMobile();

  const goSearch = () => notYetWiredToast('Search');

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      {/* clamp height so the hero is full on desktop but doesn't dominate on mobile.
          Floor dropped to 420px so the headline + a glimpse of the search widget
          both sit above the fold on a 375×812 phone-portrait viewport. */}
      <div style={{ position: 'relative', height: 'clamp(420px, 60vh, 760px)', marginTop: 'clamp(-60px, -8vh, -68px)' }}>
        {HERO_DESTINATIONS.map((d, i) => (
          <div key={d.id} style={{
            position: 'absolute', inset: 0,
            opacity: i === destIdx ? 1 : 0,
            transition: 'opacity 1s ease',
          }}>
            <img src={d.img} style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: i === destIdx ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 6s ease',
            }} alt=""/>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(180deg, rgba(10,12,16,0.5) 0%, rgba(10,12,16,0.2) 30%, rgba(10,12,16,0.85) 100%)',
            }}/>
          </div>
        ))}

        {/* Hero text */}
        <div style={{
          position: 'absolute',
          top: 'clamp(140px, 22vh, 220px)',
          left: 'clamp(20px, 5vw, 64px)',
          right: 'clamp(20px, 5vw, 64px)',
          color: '#fff',
          maxWidth: 920,
          zIndex: 2,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <span style={{ fontFamily: F, fontSize: 11, letterSpacing: 3, fontWeight: 600, opacity: 0.75 }}>
              ● NOW SHOWING — {HERO_DESTINATIONS[destIdx]?.country.toUpperCase()}
            </span>
          </div>
          <h1 style={{ fontFamily: F, fontSize: 'clamp(44px, 9vw, 132px)', fontWeight: 600, lineHeight: 0.88, margin: 0, letterSpacing: -0.05, color: '#fff' }}>
            Travel,<br/>
            <span style={{ fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 400 }}>rediscovered.</span>
          </h1>
          <p style={{ fontFamily: F, fontSize: 'clamp(15px, 2.2vw, 18px)', lineHeight: 1.5, opacity: 0.85, marginTop: 22, maxWidth: 480 }}>
            Flights, stays, and the AI that does the spreadsheet for you. No fine-print theatre.
          </p>
        </div>

        {/* Destination dot rail — hidden on narrow screens (would crowd the hero text) */}
        <div className="gf-hero-dotrail" style={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 10, zIndex: 5 }}>
          {HERO_DESTINATIONS.map((d, i) => (
            <button key={d.id} onClick={() => setDestIdx(i)} style={{
              padding: 8, borderRadius: 12, cursor: 'pointer',
              background: i === destIdx ? 'rgba(255,255,255,0.1)' : 'transparent',
              border: '1px solid', borderColor: i === destIdx ? 'rgba(255,255,255,0.2)' : 'transparent',
              backdropFilter: i === destIdx ? 'blur(20px)' : 'none',
              display: 'flex', alignItems: 'center', gap: 10, minWidth: 180,
              transition: 'all .25s', color: '#fff',
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `url(${d.img}) center/cover`, flexShrink: 0 }}/>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontFamily: F, fontSize: 13, fontWeight: 600 }}>{d.city}</div>
                <div style={{ fontFamily: F, fontSize: 10, opacity: 0.7, letterSpacing: 1 }}>
                  FROM ₹{d.price.toLocaleString('en-IN')}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Search widget — overlaps hero bottom on desktop only. On mobile it
            renders BELOW the hero (see block after this </div>) so the headline
            stays visible; absolutely-positioning a 6-field stacked form onto a
            420px hero buries the entire image. */}
        {!isMobile && (
          <div style={{
            position: 'absolute',
            bottom: -64,
            left: 'clamp(16px, 4vw, 64px)',
            right: 'clamp(16px, 4vw, 64px)',
            zIndex: 20,
          }}>
            <SearchWidget/>
          </div>
        )}
      </div>

      {/* Mobile: SearchWidget as a normal block right below the hero.
          Desktop: spacer to clear the absolutely-positioned overlap above. */}
      {isMobile ? (
        <div style={{ padding: '24px clamp(16px, 4vw, 64px) 0' }}>
          <SearchWidget/>
        </div>
      ) : (
        <div style={{ height: 100 }}/>
      )}

      {/* ── RECENT SEARCHES (authed only) ─────────────────────────────── */}
      {status === 'authed' && (
        <div style={{ padding: '8px clamp(16px, 4vw, 64px) 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0 }}>
            <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2, color: T.inkDim, fontWeight: 600, textTransform: 'uppercase' }}>
              Recent searches
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            {RECENT_SEARCHES.map(s => (
              <button
                key={`${s.from}-${s.to}`}
                onClick={goSearch}
                style={{
                  height: 38, padding: '0 16px', borderRadius: 999,
                  background: T.surface, border: `1px solid ${T.lineHi}`,
                  color: T.ink, fontFamily: F, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span style={{ color: T.inkDim, fontSize: 11 }}>{s.from}</span>
                <span style={{ color: T.inkDim }}>→</span>
                <span>{s.label}</span>
                <span style={{ color: T.inkDim, fontSize: 12 }}>· {s.date}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── STORIES STRIP ────────────────────────────────────────────────── */}
      <StoriesStrip/>

      {/* ── DEAL BANNER ──────────────────────────────────────────────────── */}
      <div style={{ padding: '48px 0 0' }}>
        <div style={{ padding: '0 clamp(16px, 4vw, 64px)' }}>
          <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2.5, color: T.inkDim, fontWeight: 600, marginBottom: 16 }}>
            DEAL OF THE DAY
          </div>
        </div>
        <DealBanner/>
      </div>

      {/* ── TRENDING DESTINATIONS GRID ───────────────────────────────────── */}
      <TrendingGrid/>

      {/* ── AI BANNER ────────────────────────────────────────────────────── */}
      <div style={{ padding: '80px clamp(16px, 4vw, 64px) 0' }}>
        <div
          onClick={() => notYetWiredToast('AI Trip Planner')}
          style={{
            position: 'relative', borderRadius: 36, overflow: 'hidden',
            padding: 'clamp(28px, 5vw, 60px) clamp(20px, 5vw, 56px)', cursor: 'pointer',
            background: `linear-gradient(135deg, rgba(232,68,26,0.13) 0%, rgba(75,142,219,0.13) 100%), ${T.surface}`,
            border: `1px solid ${T.lineHi}`,
          }}
        >
          <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, background: `radial-gradient(circle, rgba(232,68,26,0.27) 0%, transparent 70%)`, filter: 'blur(60px)' }}/>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'clamp(16px, 3vw, 40px)', flexWrap: 'wrap' }}>
            <div style={{ width: 'clamp(64px, 8vw, 96px)', height: 'clamp(64px, 8vw, 96px)', borderRadius: 32, background: 'linear-gradient(135deg, #E8441A, #4B8EDB)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 60px rgba(232,68,26,0.2)', fontSize: 'clamp(28px, 4vw, 42px)', color: T.bg }}>✦</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 3, color: T.primary, fontWeight: 700 }}>GLOBAL FLYT AI · TRIP PLANNER</div>
              <h3 style={{ fontFamily: DISPLAY, fontSize: 'clamp(28px,3.5vw,48px)', fontWeight: 500, color: T.ink, margin: '8px 0 0', letterSpacing: -0.02, lineHeight: 1 }}>
                "Plan a 7-day Bali trip under <span style={{ color: T.primary }}>₹1,25,000</span>"
              </h3>
              <p style={{ fontFamily: F, fontSize: 16, color: T.inkDim, marginTop: 12, maxWidth: 620 }}>
                Tell us a vibe, a budget, a destination. We'll cobble together flights, stays, and things to do — bookable in one tap.
              </p>
            </div>
            <button
              style={{
                height: 56, padding: '0 28px', borderRadius: 999,
                background: T.primary, color: '#fff', border: 'none',
                fontFamily: F, fontSize: 15, fontWeight: 700, cursor: 'pointer',
                flexShrink: 0,
              }}
            >Try it free →</button>
          </div>
        </div>
      </div>

      {/* ── DEALS (price drops) ───────────────────────────────────────────── */}
      <div style={{ padding: '80px clamp(16px, 4vw, 64px) 80px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2.5, color: T.accent, fontWeight: 600 }}>● PRICE DROPS · LAST 24H</div>
            <h2 style={{ fontFamily: DISPLAY, fontSize: 'clamp(32px,4vw,52px)', fontWeight: 500, color: T.ink, margin: '8px 0 0', letterSpacing: -0.025, lineHeight: 1 }}>
              Cheap right <em style={{ fontStyle: 'italic' }}>now.</em>
            </h2>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {[
            { from: 'BLR', to: 'BKK', city: 'Bangkok',   drop: -34, price: 12499, img: IMG('1506665531195-3566af2b4dfa', 600) },
            { from: 'BLR', to: 'DXB', city: 'Dubai',     drop: -22, price: 15800, img: IMG('1512453979798-5ea266f8880c', 600) },
            { from: 'BLR', to: 'CMB', city: 'Colombo',   drop: -28, price:  8200, img: IMG('1602002418082-a4443e081dd1', 600) },
            { from: 'BLR', to: 'KTM', city: 'Kathmandu', drop: -18, price:  9900, img: IMG('1605640840605-14ac1855827b', 600) },
          ].map((d, i) => (
            <div
              key={i}
              onClick={goSearch}
              style={{
                borderRadius: 24, overflow: 'hidden', cursor: 'pointer',
                background: T.surface, border: `1px solid ${T.line}`,
              }}
            >
              <div style={{ height: 160, background: `url(${d.img}) center/cover`, position: 'relative' }}>
                <div style={{
                  position: 'absolute', top: 12, left: 12,
                  padding: '4px 10px', borderRadius: 999,
                  background: T.primary, color: '#fff',
                  fontFamily: F, fontSize: 11, fontWeight: 700,
                }}>{d.drop}%</div>
              </div>
              <div style={{ padding: 18 }}>
                <div style={{ fontFamily: F, fontSize: 11, color: T.inkDim, letterSpacing: 2, fontWeight: 600 }}>{d.from} → {d.to}</div>
                <div style={{ fontFamily: DISPLAY, fontSize: 28, fontWeight: 500, color: T.ink, marginTop: 4, letterSpacing: -0.01 }}>{d.city}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 10 }}>
                  <span style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 500, color: T.ink }}>
                    ₹{d.price.toLocaleString('en-IN')}
                  </span>
                  <span style={{ fontFamily: F, fontSize: 12, color: T.inkDim }}>round-trip</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <SiteFooter/>
    </div>
  );
}
